import { listApprovals } from './vault-api-approvals.ts'
import { listVaultMessages } from './vault-api-messages.ts'
import { unreadForPrincipal } from './vault-message-threads.ts'
import type {
  AttentionEvent,
  AttentionSource,
} from './use-attention-watcher.ts'

/**
 * The two things worth interrupting Matt for, and nothing else.
 *
 * D11 names them: a new message addressed to him, and an approval
 * waiting on a human. Both block somebody — an agent is stopped dead on
 * a pending approval, and a message addressed to the operator is usually
 * an agent asking for something it cannot get on its own. Everything
 * else the console knows (a session changing state, a daemon's heartbeat,
 * an audit row) is information, and information goes on a page, not on a
 * desktop.
 *
 * The old vault had a third source — the outcome of the operator's OWN
 * requests — and it is deliberately not ported. In this product the
 * person who files a restricted request is an agent, not the operator,
 * so that source would announce the operator's own clicks back to them.
 *
 * SECURITY: an OS notification is rendered by the window manager and
 * survives on a lock screen. Nothing below may put a message BODY in
 * one. A subject line is plaintext on the wire already — the server
 * stores it unsealed precisely so an inbox is listable without the
 * tenant key — and an argv has already been shown to whoever can see the
 * approvals queue. Those are the limit.
 */

/** Keeps an untrusted string short enough to be one line of a
 * notification. Characters, not bytes: this is a display cap. */
function oneLine(text: string, max = 120): string {
  const collapsed = text.replace(/\s+/g, ' ').trim()
  return collapsed.length <= max ? collapsed : `${collapsed.slice(0, max)}…`
}

/**
 * Unread messages addressed to the operator's own principal.
 *
 * `includeConsumed: false` because the operator plane defaults it to
 * TRUE — right for an audit view, wrong for a bell, which should count a
 * working inbox. `viewerPrincipalId === null` yields nothing at all
 * rather than every unread message in the tenant; `unreadForPrincipal`
 * owns that decision and explains it.
 */
export function messageAttentionSource(
  rootId: string,
  viewerPrincipalId: string | null,
): AttentionSource {
  return {
    id: 'messages',
    load: async (): Promise<AttentionEvent[]> => {
      if (rootId === '' || viewerPrincipalId === null) return []
      const messages = await listVaultMessages(rootId, {
        limit: 50,
        includeConsumed: false,
        principalId: viewerPrincipalId,
      })
      return unreadForPrincipal(messages, viewerPrincipalId).map((message) => ({
        // The message id. Stable for the life of the event, which is
        // what the dedupe depends on — a key derived from the unread
        // COUNT or from a position in the list would re-announce the
        // same message every time another one arrived.
        key: message.id,
        title: `${message.sender.name ?? 'An agent'} sent you a vault message`,
        body:
          message.subject === ''
            ? 'No subject — open the console to read it.'
            : oneLine(message.subject),
      }))
    },
  }
}

/**
 * Approval requests sitting in `pending`.
 *
 * The same queue `/vault/approvals` renders, asked the same way. Fifty
 * is plenty: a tenant with more than fifty simultaneously-pending
 * approvals has a problem no notification is going to solve, and the
 * badge saying "50" is as actionable as the badge saying the true
 * number.
 */
export function approvalAttentionSource(rootId: string): AttentionSource {
  return {
    id: 'approvals',
    load: async (): Promise<AttentionEvent[]> => {
      if (rootId === '') return []
      const page = await listApprovals(rootId, {
        state: 'pending',
        limit: 50,
      })
      return page.requests.map((request) => ({
        key: request.id,
        title: 'A vault approval is waiting',
        // The principal and the argv: who is blocked, and on what. Both
        // are already visible to anyone who can open the queue, and the
        // argv is the single most useful thing for deciding whether this
        // is worth getting up for.
        body: oneLine(`${request.principalName} — ${request.argv.join(' ')}`),
      }))
    },
  }
}

/**
 * Both sources for one tenant root.
 *
 * Returns a NEW array on every call, which is why `useAttentionWatcher`
 * reads its sources through a ref instead of an effect dependency — see
 * the note there.
 */
export function vaultAttentionSources(
  rootId: string,
  viewerPrincipalId: string | null,
): AttentionSource[] {
  return [
    messageAttentionSource(rootId, viewerPrincipalId),
    approvalAttentionSource(rootId),
  ]
}
